import {fetchApi} from './implementations/fetch';
import {mockApi} from './implementations/mock';
import {
    ClinicObjectSlim,
    ReasonFilterObject,
    PlacesFilterObject,
    CaregiverObject,
    CapacityResponse,
    FinalizeBookingRequest,
    BookingPatchRequest,
    ConsentRequestAnswer,
    FinalizeDropInRequest,
    AccountJSON,
    ManualVaccinationPayload,
    DropinArrivalRequest,
    Country,
    SearchRequest,
    SearchResult,
    ScheduledStop
} from './types/types';
import {debug} from '../debug/debug';
import {getLocaleWithRegionCode, getLocaleWithRegionFromLang} from '../i18n/locale';
import {LatLng, LatLngBounds} from 'leaflet';
import {weekInterval} from '../../lib/dates/week';
import {isSameDay} from 'date-fns';
import {capacityCache} from '../cache/capacityCache';
import {format} from 'src/lib/dates/format';
import {scheduledStopCache} from '../cache/scheduledStopCache';

const {
    getAccount,
    createOrUpdateAccount,
    deleteAccount,
    getCaregivers,
    caregiverCookiePolicyLink,
    getClinics,
    getVisitReasons,
    getPlaces,
    getPlace,
    getCounties,
    getMunicipalities,
    getCountries,
    getClinic,
    getAppointmentCategories,
    getAppointmentType,
    getTimeSlots,
    getNextAvailableTimeSlot,
    getCapacity,
    reserveTimeSlot,
    unreserveTimeSlot,
    refreshTimeSlot,
    getHealthDeclaration,
    getDefaultHealthDeclaration,
    getPerson,
    getPersonMinimal,
    finalizeBooking,
    submitHealthDeclaration,
    validatePnr,
    validatePnrOrBirthdate,
    getSingleBooking,
    getBookings,
    getConsentRequests,
    patchConsentRequests,
    patchBooking,
    getHistory,
    addManualVaccination,
    editManualVaccination,
    deleteManuallyAddedVaccination,
    getVaccineCategories,
    getInfoTexts,
    getArrivalQrCodeBooking,
    getArrivalQrCodeDropin,
    getArrivalSettings,
    postBookingArrival,
    postDropinArrival,
    getDropInQueueOverview,
    getDropinCategories,
    getDropInArrival,
    getUserHealthdecl,
    searchWithFilters,
    getLocations,
    getLocation,
    getScheduledStopsAtLocation,
    getTimeSlotsOnLocation,
    getFirstTimeSlotAtLocation,
    getCaregiverLogo
} = debug.debugActive || process.env.NODE_ENV === 'test' ? mockApi : fetchApi;

const fetchAccount = async (userToken: string) => {
    return await getAccount(userToken);
};

const postAccount = async (userToken: string, data: AccountJSON) => {
    return await createOrUpdateAccount(userToken, data);
};

const removeAccount = async (userToken: string) => {
    return await deleteAccount(userToken);
};

const fetchVisitReasons = async () => {
    const visitReasons = await getVisitReasons();

    return visitReasons.map(({id, name, category}) => {
        return {
            id: id.toString(),
            category,
            name
        } satisfies ReasonFilterObject;
    });
};

const fetchCaregivers = async () => {
    const careGivers = await getCaregivers();

    return careGivers.map(({id, name, cookiePolicyVersion, hsaid}) => {
        return {
            id,
            label: name,
            cookiePolicyVersion,
            hsaid
        } satisfies CaregiverObject;
    });
};

const getCaregiverCookiePolicyLink = (caregiverId: number) => {
    return caregiverCookiePolicyLink(caregiverId);
};

const fetchClinics = async () => {
    let clinics = await getClinics();

    // Removing clinics without position
    clinics = clinics.filter((clinic) => clinic.position && clinic.position?.lat !== 0 && clinic.position?.lon !== 0);

    return clinics.map((clinic) => {
        return {
            id: clinic.id.toString(),
            name: clinic.name,
            availableVisitReasonIds: clinic.availableVisitReasons.map((id) => id.toString()),
            principalCareGiverId: clinic.principalCareGiver,
            logoUrl: clinic.logoUrl,
            careGiverId: clinic.careGiver,
            address: clinic.address,
            city: clinic.city,
            zip: clinic.zip,
            hasDropin: clinic.hasDropin || false,
            hasTimebook: clinic.hasTimebook || false,
            location: {
                lat: clinic.position?.lat as number,
                lng: clinic.position?.lon as number
            },
            externalBookingUrl: clinic.externalBookingUrl,
            externalBookingUrlType: clinic.externalBookingUrlType
        } satisfies ClinicObjectSlim;
    });
};

const fetchClinicById = async (id: number) => {
    return await getClinic(id);
};

const fetchSearch = async (query: string, abortSignal: AbortSignal) => {
    const search = await getPlaces(query, abortSignal);

    if ('throwIfAborted' in abortSignal) {
        // This might not be needed when using actual API endpoint
        abortSignal.throwIfAborted();
    }

    return search.map(({id, name, bounds, category}) => {
        return {
            id,
            name,
            category,
            boundingBox: new LatLngBounds(new LatLng(bounds.bottom, bounds.left), new LatLng(bounds.top, bounds.right))
        } satisfies PlacesFilterObject;
    });
};

const fetchPlace = async (placeId: string) => {
    const placeData = await getPlace(placeId);

    if (placeData === null) {
        throw new Error('Place not found');
    }

    const {id, name, category, bounds} = placeData;

    return {
        id,
        name,
        category,
        boundingBox: new LatLngBounds(new LatLng(bounds.bottom, bounds.left), new LatLng(bounds.top, bounds.right))
    } satisfies PlacesFilterObject;
};

const fetchMunicipalities = async () => {
    const municipalitiesData = await getMunicipalities();

    return municipalitiesData.map(({id, name, bounds, category}) => {
        return {
            id,
            name,
            category,
            boundingBox: new LatLngBounds(new LatLng(bounds.bottom, bounds.left), new LatLng(bounds.top, bounds.right))
        } satisfies PlacesFilterObject;
    });
};

const fetchCounties = async () => {
    const countiesData = await getCounties();

    return countiesData.map(({id, name, bounds, category}) => {
        return {
            id,
            name,
            category,
            boundingBox: new LatLngBounds(new LatLng(bounds.bottom, bounds.left), new LatLng(bounds.top, bounds.right))
        } satisfies PlacesFilterObject;
    });
};

const fetchCountries = async () => {
    const countiesData = await getCountries();
    const localeWithRegion = getLocaleWithRegionCode();
    let countriesMap = countiesData.map(({id, name}) => {
        const localeName = name.find(({locale}) => locale === localeWithRegion) || name[0];

        return {
            id: id,
            name: localeName.text
        } satisfies Country;
    });

    countriesMap.sort((a, b) => {
        // unknown country (id = -1) should always be first
        if (a.id === -1) return -1;
        if (b.id === -1) return 1;
        return a.name.localeCompare(b.name);
    });
    return countriesMap;
};

const fetchHistory = async (userToken: string, userId?: string) => {
    return getHistory(userToken, userId);
};

const addVaccination = async (userToken: string, userId: string, payload: ManualVaccinationPayload) => {
    return await addManualVaccination(userToken, userId, payload);
};

const editVaccination = async (
    userToken: string,
    userId: string,
    vaccinationId: string,
    payload: ManualVaccinationPayload
) => {
    return await editManualVaccination(userToken, userId, vaccinationId, payload);
};

const removeManuallyAddedVaccination = async (userToken: string, vaccinationId: string, userId?: string) => {
    return await deleteManuallyAddedVaccination(userToken, vaccinationId, userId);
};

const fetchVaccineCategories = async () => {
    return await getVaccineCategories();
};

const fetchAppointmentCategories = async (clinicId: number) => {
    return await getAppointmentCategories(clinicId);
};
const fetchAppointmentType = async (clinicId: number, appointmentTypeId: number) => {
    return await getAppointmentType(clinicId, appointmentTypeId);
};

const fetchTimeSlotsForType = async (
    clinicId: number,
    appointmentTypeId: number,
    from: Date,
    to: Date,
    abortSignal: AbortSignal
) => {
    const stringTimeSlots = await getTimeSlots(clinicId, appointmentTypeId, from, to, abortSignal);

    if ('throwIfAborted' in abortSignal) {
        // This might not be needed when using actual API endpoint
        abortSignal.throwIfAborted();
    }

    const weekDates = weekInterval(from);
    const timeSlotsList = stringTimeSlots.map((dateString) => new Date(dateString));
    const timeSlots = weekDates.reduce(
        (acc, date) => {
            acc.push(timeSlotsList.filter((val) => isSameDay(date, val)));

            return acc;
        },
        [] as Array<Array<Date>>
    );

    return timeSlots;
};

const fetchNextAvailableTimeSlotForType = async (clinicId: number, appointmentTypeId: number, from: Date, to: Date) => {
    const dateString = await getNextAvailableTimeSlot(clinicId, appointmentTypeId, from, to);

    if (!dateString) {
        return null;
    }

    return new Date(dateString);
};

const fetchCapacity = async (clinicId: number, abortSignal: AbortSignal, reasonIds: Array<string> = []) => {
    const capacityCacheKey = `${[clinicId, ...reasonIds.sort()]?.join(',')}`;

    if (capacityCache.has(capacityCacheKey)) {
        return capacityCache.get(capacityCacheKey) as CapacityResponse;
    }

    const capacity = await getCapacity(clinicId, abortSignal, reasonIds);

    if ('throwIfAborted' in abortSignal) {
        // This might not be needed when using actual API endpoint
        abortSignal.throwIfAborted();
    }

    if (!capacity) {
        return null;
    }

    capacityCache.set(capacityCacheKey, capacity);

    return capacity;
};

const fetchReserveTimeSlot = async (clinicId: number, appointmentTypeId: number, timeSlot: Date) => {
    const bookingId = await reserveTimeSlot(clinicId, appointmentTypeId, timeSlot);

    return bookingId;
};

const fetchRefreshTimeSlot = async (jwt: string, clinicId: number, bookingId: string) => {
    return await refreshTimeSlot(jwt, clinicId, bookingId);
};

const fetchUnreserveTimeSlot = async (clinicId: number, bookingId: string, userJwt: string | null = null) => {
    return await unreserveTimeSlot(clinicId, bookingId, userJwt);
};
const fetchPerson = async (userToken: string) => {
    return await getPerson(userToken);
};
const fetchPersonMinimal = async (userToken: string) => {
    return await getPersonMinimal(userToken);
};

const fetchHealthDeclaration = async (healthDeclarationId: number) => {
    return await getHealthDeclaration(healthDeclarationId);
};

const fetchDefaultHealthDeclaration = async (clinicId: number) => {
    return await getDefaultHealthDeclaration(clinicId);
};

const postFinalizeBooking = async (
    clinicId: number,
    bookingId: string,
    userToken: string | null,
    data: FinalizeBookingRequest
) => {
    return await finalizeBooking(clinicId, bookingId, userToken, data);
};

const postHealthDeclaration = async (clinicId: number, userToken: string | null, data: FinalizeDropInRequest) => {
    return await submitHealthDeclaration(clinicId, userToken, data);
};

const postValidatePnr = async (pnr: string) => {
    return await validatePnr(pnr);
};

const postValidateIdentity = async (id: string) => {
    return await validatePnrOrBirthdate(id);
};

const fetchSingleBooking = async (bookingCode: string, identity: string) => {
    return await getSingleBooking(bookingCode, identity);
};

const fetchBookings = async (userJwt: string) => {
    return await getBookings(userJwt);
};

const fetchConsentRequests = async (userJwt: string) => {
    return await getConsentRequests(userJwt);
};

const updateConsentRequests = async (userJwt: string, consentRequestAnswer: ConsentRequestAnswer) => {
    return await patchConsentRequests(userJwt, consentRequestAnswer);
};

const updateBooking = async (
    bookingId: string,
    clinicId: number,
    bookingData: Partial<BookingPatchRequest>,
    userJwt: string | null
) => {
    return await patchBooking(bookingId, clinicId, bookingData, userJwt);
};

const fetchInfoTexts = async () => {
    return await getInfoTexts();
};

const fetchArrivalQrCodeBooking = (clinicId: number) => {
    return getArrivalQrCodeBooking(clinicId);
};

const fetchArrivalQrCodeDropin = (clinicId: number) => {
    return getArrivalQrCodeDropin(clinicId);
};

const fetchArrivalSettings = async (clinicId: number) => {
    return await getArrivalSettings(clinicId);
};

const performBookingArrival = async (clinicId: number, identity: string, jwt: string | null) => {
    return await postBookingArrival(clinicId, identity, jwt);
};
const performDropinArrival = async (clinicId: number, data: DropinArrivalRequest, jwt: string | null) => {
    return await postDropinArrival(clinicId, data, jwt);
};

const fetchDropInQueueOverview = async (clinicId: number) => {
    return await getDropInQueueOverview(clinicId);
};

const fetchDropinCategories = async (clinicId: number) => {
    return await getDropinCategories(clinicId);
};

const fetchDropInArrival = async (clinicId: number, arrivalId: number) => {
    return await getDropInArrival(clinicId, arrivalId);
};

const fetchUserHealthdecl = async (userId: string, defId: number, jwt: string | null) => {
    return await getUserHealthdecl(userId, defId, jwt);
};

const fetchSearchWithFilters = async (params: SearchRequest): Promise<Array<SearchResult>> => {
    return await searchWithFilters(params);
};

const fetchLocations = async () => {
    return await getLocations();
};

const fetchLocation = async (locationId: number) => {
    return await getLocation(locationId);
};

const fetchScheduledStopsAtLocation = async (locationId: number, from: Date, to: Date) => {
    const scheduledStopCacheKey = `${locationId},${format(from)},${format(to)}`;
    if (scheduledStopCache.has(scheduledStopCacheKey)) {
        return scheduledStopCache.get(scheduledStopCacheKey) as Array<ScheduledStop>;
    }

    const stops = await getScheduledStopsAtLocation(locationId, from, to);

    scheduledStopCache.set(scheduledStopCacheKey, stops);
    return stops;
};

const fetchTimeSlotsOnLocation = async (locationId: number, appointmentTypes: Array<number>, from: Date, to: Date) => {
    return await getTimeSlotsOnLocation(locationId, appointmentTypes, from, to);
};

const fetchFirstTimeSlotAtLocation = async (
    locationId: number,
    appointmentTypes: Array<number>,
    from: Date,
    to: Date
) => {
    const dateString = await getFirstTimeSlotAtLocation(locationId, appointmentTypes, from, to);

    if (!dateString) {
        return null;
    }

    return new Date(dateString);
};

const fetchCaregiverLogo = async (caregiverId: number) => {
    return await getCaregiverLogo(caregiverId);
};

export {
    fetchAccount,
    postAccount,
    removeAccount,
    fetchVisitReasons,
    fetchCaregivers,
    getCaregiverCookiePolicyLink,
    fetchCaregiverLogo,
    fetchClinics,
    fetchSearch,
    fetchPlace,
    fetchClinicById,
    fetchMunicipalities,
    fetchCounties,
    fetchCountries,
    fetchHistory,
    addVaccination,
    editVaccination,
    removeManuallyAddedVaccination,
    fetchVaccineCategories,
    fetchAppointmentCategories,
    fetchAppointmentType,
    fetchTimeSlotsForType,
    fetchNextAvailableTimeSlotForType,
    fetchCapacity,
    fetchReserveTimeSlot,
    fetchUnreserveTimeSlot,
    fetchRefreshTimeSlot,
    fetchHealthDeclaration,
    fetchDefaultHealthDeclaration,
    fetchPerson,
    fetchPersonMinimal,
    postFinalizeBooking,
    postHealthDeclaration,
    postValidatePnr,
    postValidateIdentity,
    fetchSingleBooking,
    fetchBookings,
    fetchConsentRequests,
    updateConsentRequests,
    updateBooking,
    fetchInfoTexts,
    fetchArrivalQrCodeBooking,
    fetchArrivalQrCodeDropin,
    fetchArrivalSettings,
    performBookingArrival,
    performDropinArrival,
    fetchDropInQueueOverview,
    fetchDropinCategories,
    fetchDropInArrival,
    fetchUserHealthdecl,
    fetchSearchWithFilters,
    fetchLocations,
    fetchLocation,
    fetchScheduledStopsAtLocation,
    fetchTimeSlotsOnLocation,
    fetchFirstTimeSlotAtLocation
};
